import { useState, type FormEvent } from 'react';
import {
  deleteResident, useDeviceAssignments, generateResidentUid,
} from '@/shared/firebase/admin';
import { useAuth } from '@/shared/auth/AuthContext';
import { useTrackedResidents } from '@/shared/hooks/useTracked';
import { Avatar } from '@/shared/components/Avatar';
import { Modal } from '@/shared/components/Modal';

export function ResidentsTab() {
  const { centerId } = useAuth();
  const { residents, loading } = useTrackedResidents(centerId);
  const assignments = useDeviceAssignments(centerId);
  const [editing, setEditing] = useState<{ uid: string; isNew: boolean } | null>(null);

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900">Residentes ({residents.length})</h2>
          <p className="text-sm text-slate-500">Gestiona las personas a cargo de la residencia.</p>
        </div>
        <button
          onClick={() => setEditing({ uid: generateResidentUid(), isNew: true })}
          className="btn-primary"
        >+ Nuevo residente</button>
      </div>

      {loading ? (
        <p className="text-slate-500">Cargando…</p>
      ) : residents.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-slate-500">No hay residentes todavía.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {residents.map((r) => {
            const dev = assignments[r.uid];
            return (
              <li key={r.uid} className="card flex items-center gap-3">
                <Avatar name={r.config?.name || r.uid} photo={r.config?.photo} />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 truncate">{r.config?.name || r.uid}</p>
                  <p className="text-xs text-slate-500 truncate">
                    <code>{r.uid}</code>
                    {dev?.imei
                      ? <> · 📡 IMEI {dev.imei}{dev.deviceName ? ` (${dev.deviceName})` : ''}</>
                      : <span className="text-warning"> · sin dispositivo</span>}
                  </p>
                </div>
                <button onClick={() => setEditing({ uid: r.uid, isNew: false })} className="btn-ghost text-xs">Editar</button>
                <button
                  onClick={async () => {
                    if (confirm(`¿Borrar a ${r.config?.name || r.uid} y todos sus datos?`)) {
                      await deleteResident(centerId, r.uid);
                    }
                  }}
                  className="btn-danger text-xs"
                >Borrar</button>
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <ResidentForm
          centerId={centerId}
          uid={editing.uid}
          isNew={editing.isNew}
          existing={editing.isNew ? null : residents.find((r) => r.uid === editing.uid) || null}
          existingDevice={assignments[editing.uid]}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ResidentForm({
  centerId, uid, isNew, existing, existingDevice, onClose,
}: {
  centerId: string; uid: string; isNew: boolean;
  existing: { config?: { name?: string; resp?: string; photo?: string } } | null;
  existingDevice?: { imei: string; deviceName?: string; deviceModel?: string; simNumber?: string };
  onClose: () => void;
}) {
  const cfg = existing?.config || {};
  const [name, setName] = useState(cfg.name || '');
  const [photo, setPhoto] = useState(cfg.photo || '');

  const [imei, setImei] = useState(existingDevice?.imei || '');
  const [deviceName, setDeviceName] = useState(existingDevice?.deviceName || '');
  const [deviceModel, setDeviceModel] = useState(existingDevice?.deviceModel || 'EV-07');
  const [simNumber, setSimNumber] = useState(existingDevice?.simNumber || '');

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      const { saveResident, assignDevice, unassignDevice } = await import('@/shared/firebase/admin');
      await saveResident(centerId, uid, { name, photo });
      if (imei.trim()) {
        await assignDevice(centerId, uid, { imei, deviceName, deviceModel, simNumber });
      } else if (existingDevice) {
        await unassignDevice(centerId, uid);
      }
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setSaving(false); }
  }

  return (
    <Modal onClose={onClose}>
      <h3 className="text-lg font-bold mb-1">{isNew ? 'Nuevo residente' : 'Editar residente'}</h3>
      <p className="text-xs text-slate-500 mb-3">ID <code>{uid}</code></p>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="label">Nombre</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre completo" />
        </div>
        <div>
          <label className="label">Foto (URL, opcional)</label>
          <input className="input" value={photo} onChange={(e) => setPhoto(e.target.value)} placeholder="https://…" />
        </div>

        <fieldset className="border-t border-slate-100 pt-3">
          <legend className="label">Dispositivo GPS (opcional)</legend>
          <input className="input mb-2" value={imei} onChange={(e) => setImei(e.target.value.replace(/\D/g, ''))} placeholder="IMEI (15 dígitos)" />
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input className="input" value={deviceName} onChange={(e) => setDeviceName(e.target.value)} placeholder="Nombre dispositivo" />
            <input className="input" value={deviceModel} onChange={(e) => setDeviceModel(e.target.value)} placeholder="Modelo" />
          </div>
          <input className="input" value={simNumber} onChange={(e) => setSimNumber(e.target.value)} placeholder="Nº SIM (opcional)" />
        </fieldset>

        {err && <p className="text-sm text-danger">{err}</p>}
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving} className="btn-primary flex-1">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}
